import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Search, MapPin, Loader2 } from "lucide-react";

import { addressesService } from "../../../../services/addresses";
import { lookupCityByPlz, lookupPlzByCity } from "../../../../services/plzLookup";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Checkbox } from "../../../../components/ui/checkbox";
import { PhoneInput } from "../../../../components/forms/PhoneInput";
import { CountrySelect } from "../../../../components/forms/CountrySelect";

/**
 * A12 wizard Step 1 — Addresses.
 *
 * Two-mode hybrid per address block:
 *   • Search field at the top → GET /api/addresses?q=… → click a result to
 *     prefill all fields.
 *   • Manual entry stays editable underneath. Any post-pick edit clears
 *     the "picked from book" pill (address is now a copy).
 *
 * A "Save to address book" checkbox lives under each block; on order
 * submit the wizard POSTs the untracked or modified addresses (see the
 * `savePlan` prop wiring below).
 *
 * PLZ ↔ city autofill via openplzapi.org fires on blur (DE-only, debounced,
 * best-effort — silent fallback on failure).
 */

const EMPTY_INLINE = {
  company: "",
  street: "",
  postal_code: "",
  city: "",
  country: "DE",
  vat_id: "",
  contact_phone: "",
  contact_email: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isMinimallyValid(a) {
  if (!a) return false;
  return (
    (a.company || "").trim().length >= 2 &&
    (a.street || "").trim() &&
    (a.postal_code || "").trim().length >= 4 &&
    (a.city || "").trim() &&
    (a.contact_phone || "").replace(/\D/g, "").length >= 6 &&
    EMAIL_RE.test((a.contact_email || "").trim())
  );
}

export { isMinimallyValid };

function addressesEqualIgnoringMeta(a, b) {
  if (!a || !b) return false;
  const norm = (s) => (s || "").trim().toLowerCase();
  return (
    norm(a.company) === norm(b.company) &&
    norm(a.street) === norm(b.street) &&
    norm(a.postal_code) === norm(b.postal_code) &&
    norm(a.city) === norm(b.city) &&
    norm(a.country) === norm(b.country) &&
    norm(a.contact_phone) === norm(b.contact_phone) &&
    norm(a.contact_email) === norm(b.contact_email) &&
    norm(a.vat_id) === norm(b.vat_id)
  );
}

function AddressBlock({ label, testIdRoot, value, onChange, saveToBook, onSaveToBookChange, t, errors, clearError }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [pickedOrigin, setPickedOrigin] = useState(null); // snapshot of picked address for change detection
  const [plzSuggestions, setPlzSuggestions] = useState([]);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const searchTimer = useRef(null);
  const plzTimer = useRef(null);
  const cityTimer = useRef(null);

  const inline = value || EMPTY_INLINE;
  const pickedStillClean = pickedOrigin && addressesEqualIgnoringMeta(pickedOrigin, inline);

  // ── Debounced search ────────────────────────────────────────────
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return undefined;
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const d = await addressesService.list({ q: query.trim(), page_size: 8 });
        setResults(d.items || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [query]);

  const pickResult = (a) => {
    const { id, created_by, created_by_name, created_at, updated_at, updated_by, ...clean } = a; // eslint-disable-line no-unused-vars
    // Ensure all fields are strings (backend nulls → "").
    const norm = {
      company: clean.company || "",
      street: clean.street || "",
      city: clean.city || "",
      postal_code: clean.postal_code || "",
      country: clean.country || "DE",
      vat_id: clean.vat_id || "",
      contact_phone: clean.contact_phone || "",
      contact_email: clean.contact_email || "",
    };
    onChange(norm);
    setPickedOrigin(norm);
    setQuery("");
    setResults([]);
    setShowDropdown(false);
    // If the user just picked one, they don't need to save it back.
    onSaveToBookChange(false);
  };

  const setField = (k) => (e) => {
    const v = e && e.target ? e.target.value : e;
    onChange({ ...inline, [k]: v });
    // A18.2 — reset any red error styling as soon as the user edits
    // the offending field, without waiting for another Continue press.
    if (clearError) clearError(k);
  };

  const errs = errors || {};
  const errCls = (field) =>
    errs[field] ? "border-status-unavailable focus-visible:ring-status-unavailable" : "";

  // ── PLZ blur → city autofill ────────────────────────────────────
  const onPlzBlur = useCallback(async () => {
    if ((inline.country || "").toUpperCase() !== "DE") return;
    if (plzTimer.current) clearTimeout(plzTimer.current);
    plzTimer.current = setTimeout(async () => {
      const cities = await lookupCityByPlz(inline.postal_code);
      if (cities.length === 1 && !(inline.city || "").trim()) {
        onChange({ ...inline, city: cities[0] });
      } else if (cities.length > 1) {
        setCitySuggestions(cities);
      } else {
        setCitySuggestions([]);
      }
    }, 300);
  }, [inline, onChange]);

  // ── City blur → PLZ suggestions ─────────────────────────────────
  const onCityBlur = useCallback(async () => {
    if ((inline.country || "").toUpperCase() !== "DE") return;
    if (cityTimer.current) clearTimeout(cityTimer.current);
    cityTimer.current = setTimeout(async () => {
      const codes = await lookupPlzByCity(inline.city);
      if (codes.length > 0 && !(inline.postal_code || "").trim()) {
        setPlzSuggestions(codes);
      } else {
        setPlzSuggestions([]);
      }
    }, 300);
  }, [inline]);

  // Note: minValid gating on the "save to book" checkbox was retired in
  // the A12 tester follow-up (permanently-disabled false negatives).
  // `isMinimallyValid` still runs at submit time via `computeSavePlan`
  // so an incomplete block never actually POSTs to the address book.

  return (
    <div className="space-y-4" data-testid={`${testIdRoot}-block`}>
      <div>
        <h3 className="text-base font-semibold text-text-primary">{label}</h3>
      </div>

      {/* Search bar */}
      <div className="relative">
        <Label className="text-xs font-medium uppercase tracking-wide text-text-secondary">
          {t("wizard.step1.address_search_label")}
        </Label>
        <div className="relative mt-1.5">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary" />
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            placeholder={t("wizard.step1.address_search_placeholder")}
            data-testid={`${testIdRoot}-search`}
            className="h-10 pl-9 bg-surface border-border-subtle focus-visible:ring-champagne"
          />
          {searching && (
            <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-text-secondary" />
          )}
        </div>
        {showDropdown && query.trim() && (
          <div
            className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-border-subtle bg-surface shadow-elevated"
            data-testid={`${testIdRoot}-search-dropdown`}
          >
            {results.length === 0 && !searching ? (
              <div className="px-3 py-2 text-xs text-text-secondary">
                {t("wizard.step1.address_search_no_results")}
              </div>
            ) : (
              results.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  // A17.1 fix: `preventDefault` on the mousedown stops
                  // the click from stealing focus away from the search
                  // input (which would otherwise unmount this dropdown
                  // before the click event fires). The actual pick then
                  // runs on `onClick`, which is what @testing-library,
                  // Playwright and every real mouse produce.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickResult(r)}
                  data-testid={`${testIdRoot}-search-result-${r.id}`}
                  className="flex w-full items-start gap-2 border-b border-border-subtle px-3 py-2 text-left text-sm last:border-b-0 hover:bg-offwhite"
                >
                  <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-champagne" />
                  <div className="min-w-0">
                    <div className="truncate font-medium text-text-primary">{r.company}</div>
                    <div className="truncate text-xs text-text-secondary">
                      {r.street} · {r.postal_code} {r.city}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* A18.1 — "Filled from address book" hint retired. The
          `pickedStillClean` state stays around because it still gates
          the "Save Address" checkbox (a re-pick should not offer to
          save the same address back). It just no longer paints a pill. */}

      {/* Manual fields */}
      <div className="grid grid-cols-1 gap-3">
        <Field label={t("wizard.step1.company")} required error={errs.company} testIdRoot={`${testIdRoot}-company`}>
          <Input
            value={inline.company}
            onChange={setField("company")}
            data-testid={`${testIdRoot}-company`}
            className={`h-10 bg-surface border-border-subtle ${errCls("company")}`}
          />
        </Field>
        <Field label={t("wizard.step1.street")} required error={errs.street} testIdRoot={`${testIdRoot}-street`}>
          <Input
            value={inline.street}
            onChange={setField("street")}
            data-testid={`${testIdRoot}-street`}
            className={`h-10 bg-surface border-border-subtle ${errCls("street")}`}
          />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label={t("wizard.step1.postal_code")} required error={errs.postal_code} testIdRoot={`${testIdRoot}-postal`}>
            <Input
              value={inline.postal_code}
              onChange={setField("postal_code")}
              onBlur={onPlzBlur}
              data-testid={`${testIdRoot}-postal`}
              className={`h-10 bg-surface border-border-subtle ${errCls("postal_code")}`}
            />
            {plzSuggestions.length > 0 && (
              <div
                className="mt-1 flex flex-wrap gap-1"
                data-testid={`${testIdRoot}-plz-suggestions`}
              >
                <span className="text-[11px] text-text-secondary">
                  {t("wizard.step1.plz_suggestions_label")}:
                </span>
                {plzSuggestions.slice(0, 6).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => {
                      onChange({ ...inline, postal_code: p });
                      setPlzSuggestions([]);
                    }}
                    className="rounded bg-offwhite px-1.5 py-0.5 text-[11px] font-mono text-text-primary hover:bg-champagne/30"
                    data-testid={`${testIdRoot}-plz-suggestion-${p}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </Field>
          <Field label={t("wizard.step1.city")} required error={errs.city} testIdRoot={`${testIdRoot}-city`} className="sm:col-span-2">
            <Input
              value={inline.city}
              onChange={setField("city")}
              onBlur={onCityBlur}
              data-testid={`${testIdRoot}-city`}
              className={`h-10 bg-surface border-border-subtle ${errCls("city")}`}
            />
            {citySuggestions.length > 0 && (
              <div
                className="mt-1 flex flex-wrap gap-1"
                data-testid={`${testIdRoot}-city-suggestions`}
              >
                <span className="text-[11px] text-text-secondary">
                  {t("wizard.step1.city_suggestions_label")}:
                </span>
                {citySuggestions.slice(0, 6).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => {
                      onChange({ ...inline, city: c });
                      setCitySuggestions([]);
                    }}
                    className="rounded bg-offwhite px-1.5 py-0.5 text-[11px] text-text-primary hover:bg-champagne/30"
                    data-testid={`${testIdRoot}-city-suggestion-${c}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label={t("wizard.step1.country")} required error={errs.country} testIdRoot={`${testIdRoot}-country`}>
            <CountrySelect
              value={inline.country}
              onChange={(iso2) => {
                onChange({ ...inline, country: iso2 });
                if (clearError) clearError("country");
              }}
              testIdRoot={`${testIdRoot}-country`}
            />
          </Field>
          <Field label={t("wizard.step1.vat_id")} className="sm:col-span-2">
            <Input
              value={inline.vat_id}
              onChange={setField("vat_id")}
              data-testid={`${testIdRoot}-vat`}
              className="h-10 bg-surface border-border-subtle"
            />
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t("wizard.step1.contact_phone")} required error={errs.contact_phone} testIdRoot={`${testIdRoot}-phone`}>
            <PhoneInput
              value={inline.contact_phone}
              onChange={(v) => {
                onChange({ ...inline, contact_phone: v });
                if (clearError) clearError("contact_phone");
              }}
              testIdRoot={`${testIdRoot}-phone`}
            />
          </Field>
          <Field label={t("wizard.step1.contact_email")} required error={errs.contact_email} testIdRoot={`${testIdRoot}-email`}>
            <Input
              type="email"
              value={inline.contact_email}
              onChange={setField("contact_email")}
              data-testid={`${testIdRoot}-email`}
              className={`h-10 bg-surface border-border-subtle ${errCls("contact_email")}`}
            />
          </Field>
        </div>
      </div>

      {/* Save-to-book checkbox. A12 fix: no `disabled` gate. The user's
          intent to save is captured freely; `computeSavePlan` filters out
          any block that fails `isMinimallyValid` at submit time, so an
          incomplete block never triggers a POST. This also removes an
          entire class of "checkbox stays disabled" false negatives from
          subtle predicate drift (PhoneInput format, EmailStr rules, etc.). */}
      <label className={`flex items-center gap-2 text-sm ${pickedStillClean ? "cursor-not-allowed" : ""}`}>
        <Checkbox
          checked={saveToBook && !pickedStillClean}
          disabled={pickedStillClean}
          onCheckedChange={(v) => onSaveToBookChange(Boolean(v))}
          data-testid={`${testIdRoot}-save-to-book`}
          className="border-border-subtle data-[state=checked]:bg-champagne data-[state=checked]:text-charcoal data-[state=checked]:border-champagne disabled:cursor-not-allowed disabled:opacity-40"
        />
        <span className={pickedStillClean ? "text-text-secondary/70" : "text-text-primary"}>
          {t("wizard.step1.save_to_address_book")}
        </span>
      </label>
    </div>
  );
}

function Field({ label, required, className = "", children, error, testIdRoot }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label
        className={`text-xs font-medium uppercase tracking-wide ${
          error ? "text-status-unavailable" : "text-text-secondary"
        }`}
      >
        {label}
        {required && <span className="ml-0.5 text-status-unavailable">*</span>}
      </Label>
      {children}
      {error && (
        <p
          className="text-xs text-status-unavailable"
          data-testid={testIdRoot ? `${testIdRoot}-error` : undefined}
        >
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * StepAddresses — the wizard step. Keeps `form.customer_address_inline`
 * and (optionally) `form.delivery_address_inline` in sync. Save-to-book
 * flags are tracked in a side-store on form (`_save_customer_to_book`,
 * `_save_delivery_to_book`) so the parent wizard can consume them at
 * submit time without leaking into the API payload.
 */
export const StepAddresses = forwardRef(function StepAddresses({ form, setForm, t }, ref) {
  // A17.1 fix: functional setState. Two back-to-back setForm calls
  // (onChange + onSaveToBookChange) inside `pickResult` were both
  // spreading the SAME stale `form` closure, so the second call was
  // reverting the just-picked `customer_address_inline` back to its
  // pre-pick value. Using the updater form keeps every merge atomic.
  const setCustomer = (addr) =>
    setForm((f) => ({
      ...f,
      customer_address_id: null,
      customer_address_inline: addr,
    }));
  const setDelivery = (addr) =>
    setForm((f) => ({
      ...f,
      delivery_address_id: null,
      delivery_address_inline: addr,
    }));

  // A18.2 — per-field error state populated only when the user presses
  // Continue with an invalid block. Cleared on typing (see AddressBlock)
  // and wiped on every successful advance.
  const [errors, setErrors] = useState({ customer: {}, delivery: {} });

  const clearErrorFor = (block) => (field) =>
    setErrors((e) => {
      if (!e[block] || !e[block][field]) return e;
      const next = { ...e[block] };
      delete next[field];
      return { ...e, [block]: next };
    });

  useImperativeHandle(
    ref,
    () => ({
      /** Run per-field validation; return true when both blocks pass.
       *  On failure, paint the errors, focus the first invalid input,
       *  and DO NOT advance. */
      validateAndFocus: () => {
        const custErrs = _validateBlock(form.customer_address_inline, t);
        const delErrs = form.delivery_same_as_customer
          ? {}
          : _validateBlock(form.delivery_address_inline, t);
        setErrors({ customer: custErrs, delivery: delErrs });
        const ok = Object.keys(custErrs).length === 0 && Object.keys(delErrs).length === 0;
        if (!ok) {
          const target = Object.keys(custErrs).length > 0 ? "customer" : "delivery";
          const first =
            Object.keys(custErrs).length > 0
              ? Object.keys(custErrs)[0]
              : Object.keys(delErrs)[0];
          const testId = _firstInvalidTestId(target, first);
          const el = testId && document.querySelector(`[data-testid="${testId}"]`);
          if (el && typeof el.focus === "function") el.focus();
          else if (el && el.scrollIntoView) el.scrollIntoView({ block: "center" });
        }
        return ok;
      },
      /** Wipe every error — called on successful advance. */
      resetErrors: () => setErrors({ customer: {}, delivery: {} }),
    }),
    [form, t]
  );

  return (
    <div className="space-y-8" data-testid="step-addresses">
      <AddressBlock
        label={t("wizard.customer_heading")}
        testIdRoot="wizard-customer"
        value={form.customer_address_inline}
        onChange={setCustomer}
        saveToBook={Boolean(form._save_customer_to_book)}
        onSaveToBookChange={(v) =>
          setForm((f) => ({ ...f, _save_customer_to_book: v }))
        }
        t={t}
        errors={errors.customer}
        clearError={clearErrorFor("customer")}
      />

      <label className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={form.delivery_same_as_customer}
          onCheckedChange={(v) =>
            setForm((f) => ({ ...f, delivery_same_as_customer: Boolean(v) }))
          }
          data-testid="wizard-delivery-same"
          className="border-border-subtle data-[state=checked]:bg-champagne data-[state=checked]:text-charcoal data-[state=checked]:border-champagne"
        />
        <span>{t("wizard.delivery_same")}</span>
      </label>

      {!form.delivery_same_as_customer && (
        <AddressBlock
          label={t("wizard.delivery_heading")}
          testIdRoot="wizard-delivery"
          value={form.delivery_address_inline}
          onChange={setDelivery}
          saveToBook={Boolean(form._save_delivery_to_book)}
          onSaveToBookChange={(v) =>
            setForm((f) => ({ ...f, _save_delivery_to_book: v }))
          }
          t={t}
          errors={errors.delivery}
          clearError={clearErrorFor("delivery")}
        />
      )}
    </div>
  );
});

// Field-by-field validation. Message keys are inline so we can pass a
// single localised string per error to the AddressBlock UI.
function _validateBlock(a, t) {
  const errs = {};
  const req = t("wizard.step1.err_required");
  if ((a?.company || "").trim().length < 2) errs.company = req;
  if (!(a?.street || "").trim()) errs.street = req;
  if ((a?.postal_code || "").trim().length < 4)
    errs.postal_code = t("wizard.step1.err_postal");
  if (!(a?.city || "").trim()) errs.city = req;
  if (!(a?.country || "").trim()) errs.country = req;
  if ((a?.contact_phone || "").replace(/\D/g, "").length < 6)
    errs.contact_phone = t("wizard.step1.err_phone");
  if (!EMAIL_RE.test((a?.contact_email || "").trim()))
    errs.contact_email = t("wizard.step1.err_email");
  return errs;
}

function _firstInvalidTestId(target, field) {
  const root = target === "customer" ? "wizard-customer" : "wizard-delivery";
  // PhoneInput/CountrySelect internals don't expose the raw input; the
  // fallback is the field wrapper's testid which we scroll into view.
  return `${root}-${field.replace(/_/g, "-")}`;
}

// Utility export for the wizard page: extract the addresses that need
// to be POSTed to the global book before order create.
export function computeSavePlan(form) {
  const plan = [];
  if (form._save_customer_to_book && isMinimallyValid(form.customer_address_inline)) {
    plan.push(form.customer_address_inline);
  }
  if (
    !form.delivery_same_as_customer &&
    form._save_delivery_to_book &&
    isMinimallyValid(form.delivery_address_inline)
  ) {
    plan.push(form.delivery_address_inline);
  }
  return plan;
}
